import React, { useState } from "react";
import { useSettings } from "@/context/SettingsContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Phone, Mail, MapPin, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Contact() {
  const { settings } = useSettings();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message || "Thank you! We will get back to you shortly.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send your message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-14 lg:py-20" data-testid="contact-page">
      <div className="text-center max-w-2xl mx-auto mb-14">
        <p className="overline text-gold-dark mb-2">Get in Touch</p>
        <h1 className="font-serif text-4xl lg:text-5xl text-obsidian">Contact Us</h1>
        <p className="text-muted-foreground mt-4">Have a question about a chain or your order? We would love to help.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-12">
        <div className="space-y-6">
          {[
            { icon: Phone, label: "Phone", value: settings.contact_phone },
            { icon: Mail, label: "Email", value: settings.contact_email },
            { icon: MapPin, label: "Address", value: settings.address },
          ].filter((x) => x.value).map((x) => (
            <div key={x.label} className="flex items-start gap-4 bg-white border border-black/10 p-6">
              <div className="w-11 h-11 flex items-center justify-center bg-obsidian text-gold rounded-sm shrink-0">
                <x.icon className="w-5 h-5" />
              </div>
              <div>
                <p className="overline text-muted-foreground mb-1">{x.label}</p>
                <p className="text-obsidian font-medium">{x.value}</p>
              </div>
            </div>
          ))}
        </div>

        <form onSubmit={submit} className="bg-white border border-black/10 p-8 space-y-5" data-testid="contact-form">
          <div>
            <Label htmlFor="c-name">Name</Label>
            <Input id="c-name" value={form.name} onChange={set("name")} required className="mt-1.5 rounded-sm" data-testid="contact-name" />
          </div>
          <div>
            <Label htmlFor="c-email">Email</Label>
            <Input id="c-email" type="email" value={form.email} onChange={set("email")} required className="mt-1.5 rounded-sm" data-testid="contact-email" />
          </div>
          <div>
            <Label htmlFor="c-msg">Message</Label>
            <Textarea id="c-msg" rows={5} value={form.message} onChange={set("message")} required className="mt-1.5 rounded-sm" data-testid="contact-message" />
          </div>
          <button type="submit" disabled={sending} className="w-full inline-flex items-center justify-center gap-2 bg-obsidian text-white font-semibold py-3.5 hover:bg-gold hover:text-obsidian transition-colors disabled:opacity-50 rounded-sm" data-testid="contact-submit">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {sending ? "Sending…" : "Send Message"}
          </button>
        </form>
      </div>
    </div>
  );
}
